import { Empty, Spin, Image, Typography, Modal, ModalProps, Collapse, Space, Divider } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';

import useAPIQuery from '../../api/useAPIQuery';
import CandidateFile from '../../components/CandidateFile';
import CandidateFileContent from '../../components/CandidateFileContent';
import ErrorResult from '../../components/ErrorResult';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { AICandidateResumeSummary } from '../../sinapse/generate_resume_summary';
import { CandidateDetail, CandidateDisabilityCollection, CandidateId } from '../../types';
import styles from './CandidateAttachment.module.css';

interface FullScreenFileModalProps extends ModalProps {
  file: CandidateDetail['attachment'] | CandidateDisabilityCollection['report'];
  tenant: string;
  access_token?: string;
}

const FullScreenFileModal = ({
  file,
  tenant,
  access_token,
  ...props
}: FullScreenFileModalProps) => {
  return (
    <Modal width="80%" footer={null} closable={false} centered {...props}>
      {file?.mimetype === 'application/pdf' && (
        <iframe
          data-testid="attachment-iframe"
          height="100%"
          width="100%"
          src={`${window.location.origin}/${tenant}/download/${file.name}?type=iframe`}
          style={{ overflow: 'hidden', flex: 1 }}
        />
      )}
      {['image/png', 'image/jpeg', 'image/webp'].includes(file?.mimetype || '') && (
        <Image
          width="100%"
          preview={false}
          src={`${window.location.origin}/${tenant}/download/${file?.name}?type=iframe`}
        />
      )}
      {!file && <Empty />}
      {file &&
        access_token &&
        [
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/msword',
        ].includes(file?.mimetype || '') && (
          <iframe
            width="100%"
            height="100%"
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${window.location.origin}/${tenant}/download-officeapps-file/${file.name}?access_token=${access_token}
                `}
            frameBorder="0"
          />
        )}
    </Modal>
  );
};

const CandidateAttachment = () => {
  const { tenant, applicant_id } = useTypedParams({ tenant: String, applicant_id: CandidateId });

  const [isFullScreenVisible, setIsFullScreenVisible] = useState<boolean>(false);
  const [fullScreenFile, setFullScreenFile] = useState<
    CandidateDetail['attachment'] | CandidateDisabilityCollection['report']
  >(null);
  const [fullScreenAccessToken, setFullScreenAccessToken] = useState<string>('');

  const config = useAPIQuery('/configs/tenant-configurations/');

  const candidate = useAPIQuery(`/candidates/${applicant_id}/`);

  const attachment = candidate.data?.attachment ?? null;

  const candidateDisabilities = useAPIQuery([
    '/candidate-disability/',
    {
      params: {
        candidate: applicant_id,
      },
    },
  ]);

  const hasAnyPCDReport = candidateDisabilities.data?.results.some(
    (disability) =>
      disability.has_report ||
      (config.data?.enableAntivirusVerification && disability.antivirus_file_status !== 'BEFORE_AV')
  );

  const disabilityDict = {
    HEARING: 'Auditivo',
    INTELLECTUAL: 'Intelectual',
    MENTAL: 'Mental',
    OTHER: 'Outra',
    PHYSICAL: 'Físico',
    VISUAL: 'Visual',
  };

  return (
    <>
      <FullScreenFileModal
        key={`FullScreenFileModal-${candidate.data?.id}-${fullScreenFile?.name}`}
        file={fullScreenFile}
        tenant={tenant}
        access_token={fullScreenAccessToken}
        open={isFullScreenVisible}
        onCancel={() => {
          setIsFullScreenVisible(false);
          setFullScreenFile(null);
          setFullScreenAccessToken('');
        }}
        className={styles.modal}
        destroyOnClose
      />
      <ErrorResult error={candidate.error}>
        <Spin spinning={candidate.isLoading} wrapperClassName={styles.spin}>
          <Helmet>
            <title>Anexos | {candidate.data?.name || '...'} | Recrutamento e Seleção</title>
          </Helmet>
          <Collapse
            ghost
            className={styles.collapse}
            expandIconPosition="end"
            defaultActiveKey={['resume']}
          >
            <Collapse.Panel
              header={<Typography.Title level={3}>Currículo</Typography.Title>}
              key="resume"
            >
              {['CANDIDATE_ATTACHMENT', 'BOTH_TABS'].includes(
                config.data?.candidateResumeSummaryPlacement || ''
              ) ? (
                <AICandidateResumeSummary candidateId={applicant_id} />
              ) : null}
              <CandidateFile
                file={attachment}
                has_file={candidate.data?.has_attachment}
                tenant={tenant}
                enableAntivirusVerification={config.data?.enableAntivirusVerification}
                antivirusFileStatus={candidate.data?.antivirus_file_status}
                setIsFullScreenVisible={setIsFullScreenVisible}
                setFullScreenFile={setFullScreenFile}
                setFullScreenAccessToken={setFullScreenAccessToken}
              />
              <CandidateFileContent candidate={candidate.data} />
            </Collapse.Panel>
            {hasAnyPCDReport && (
              <Collapse.Panel
                header={<Typography.Title level={3}>Laudos médicos</Typography.Title>}
                key="pcd_reports"
              >
                <Space direction="vertical" size="middle" split={<Divider type="horizontal" />}>
                  {candidateDisabilities.data?.results.map((disability) => {
                    if (
                      disability.has_report ||
                      (config.data?.enableAntivirusVerification &&
                        disability.antivirus_file_status !== 'BEFORE_AV')
                    ) {
                      return (
                        <CandidateFile
                          file={disability.report}
                          has_file={disability.has_report}
                          tenant={tenant}
                          enableAntivirusVerification={config.data?.enableAntivirusVerification}
                          antivirusFileStatus={disability.antivirus_file_status}
                          fileHeader={
                            <>
                              {disabilityDict[disability.type]} -{' '}
                              {disability.cid ? `CID: ${disability.cid}` : 'CID não informado'}
                            </>
                          }
                          emptyFileReactComponent={<></>}
                          setIsFullScreenVisible={setIsFullScreenVisible}
                          setFullScreenFile={setFullScreenFile}
                          setFullScreenAccessToken={setFullScreenAccessToken}
                        />
                      );
                    }
                  })}
                </Space>
              </Collapse.Panel>
            )}
          </Collapse>
        </Spin>
      </ErrorResult>
    </>
  );
};

export default withRouteChange()(CandidateAttachment);
